/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/custom/InteractiveMap.tsx
 * Release: 1.0.5 - Caricamento dinamico script Google Maps
 * Data: 27/06/2025
 */

import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { GOOGLE_MAPS_CONFIG } from '../../../lib/config';
import { loadGoogleMaps } from '../../../utils/googleMapsLoader';

interface InteractiveMapProps {
  lat?: number;
  lng?: number;
  address?: string;
  disabled?: boolean;
  required?: boolean;
}

export const InteractiveMap: React.FC<InteractiveMapProps> = ({
  lat,
  lng,
  disabled = false,
  required = false
}) => {
      // Log rimossi per pulizia console
  
  const [hasError, setHasError] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentLat, setCurrentLat] = useState(GOOGLE_MAPS_CONFIG.DEFAULT_LAT);
  const [currentLng, setCurrentLng] = useState(GOOGLE_MAPS_CONFIG.DEFAULT_LNG);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const scriptLoadedRef = useRef(false);

  // Carica Google Maps usando il loader centralizzato
  useEffect(() => {
    if (!GOOGLE_MAPS_CONFIG.API_KEY || scriptLoadedRef.current || GOOGLE_MAPS_CONFIG.DISABLED) {
      if (GOOGLE_MAPS_CONFIG.DISABLED) {
        setIsLoading(false);
      }
      return;
    }

    const loadMaps = async () => {
      try {
        await loadGoogleMaps();
        scriptLoadedRef.current = true;
        setIsGoogleMapsLoaded(true);
        setIsLoading(false);
      } catch (error) {
        console.error('❌ Errore nel caricamento di Google Maps:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    loadMaps();
  }, [GOOGLE_MAPS_CONFIG.API_KEY]);

  // Verifica se Google Maps è caricato
  useEffect(() => {
    if (!scriptLoadedRef.current) return;

    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setIsGoogleMapsLoaded(true);
        setIsLoading(false);
      } else {
        setTimeout(checkGoogleMaps, 500);
      }
    };

    checkGoogleMaps();
  }, [scriptLoadedRef.current]);

  // Funzione per aggiornare la posizione della mappa
  const updateMapLocation = (newLat: number, newLng: number) => {
    // Valida che le coordinate siano numeri finiti
    if (!isFinite(newLat) || !isFinite(newLng) || isNaN(newLat) || isNaN(newLng)) {
      console.warn('[InteractiveMap] ⚠️ Coordinate non valide ricevute:', { newLat, newLng });
      return;
    }
    
    console.log('[InteractiveMap] 📍 Aggiornamento mappa con coordinate valide:', { newLat, newLng });
    setCurrentLat(newLat);
    setCurrentLng(newLng);
    
    if (mapRef.current && window.google) {
      const newPosition = new window.google.maps.LatLng(newLat, newLng);
      mapRef.current.setCenter(newPosition);
      
      if (markerRef.current) {
        markerRef.current.position = newPosition;
      } else {
        // Crea nuovo marker AdvancedMarkerElement se non esiste
        if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
          const markerDiv = document.createElement('div');
          markerDiv.innerHTML = `
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="24" fill="#EF4444" opacity="0.2"/>
              <circle cx="24" cy="24" r="18" fill="#EF4444" opacity="0.4"/>
              <circle cx="24" cy="24" r="12" fill="#EF4444"/>
              <circle cx="24" cy="24" r="6" fill="white"/>
            </svg>
          `;
          markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
            map: mapRef.current,
            position: newPosition,
            content: markerDiv
          });
        }
      }
    }
  };

  // Registra la funzione globale per essere chiamata dal campo di ricerca
  useEffect(() => {
    window.updateMapLocation = updateMapLocation;
    
    return () => {
      delete window.updateMapLocation;
    };
  }, []);

  // Aggiorna le coordinate quando cambiano le props
  useEffect(() => {
    if (lat && lng) {
      setCurrentLat(lat);
      setCurrentLng(lng);
    } else {
      // Usa le coordinate di default se non sono specificate
      setCurrentLat(GOOGLE_MAPS_CONFIG.DEFAULT_LAT);
      setCurrentLng(GOOGLE_MAPS_CONFIG.DEFAULT_LNG);
    }
  }, [lat, lng]);

  // Inizializza la mappa
  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapContainerRef.current || !GOOGLE_MAPS_CONFIG.API_KEY || !window.google) {
      return;
    }

    // Log rimosso per pulizia console

    try {
      // Stile mappa dark
      const darkMapStyle = [
        { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
        {
          featureType: 'administrative.locality',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#d59563' }]
        },
        {
          featureType: 'poi',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#d59563' }]
        },
        {
          featureType: 'poi.park',
          elementType: 'geometry',
          stylers: [{ color: '#263c3f' }]
        },
        {
          featureType: 'poi.park',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#6b9a76' }]
        },
        {
          featureType: 'road',
          elementType: 'geometry',
          stylers: [{ color: '#38414e' }]
        },
        {
          featureType: 'road',
          elementType: 'geometry.stroke',
          stylers: [{ color: '#212a37' }]
        },
        {
          featureType: 'road',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#9ca5b3' }]
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry',
          stylers: [{ color: '#746855' }]
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry.stroke',
          stylers: [{ color: '#1f2835' }]
        },
        {
          featureType: 'road.highway',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#f3d19c' }]
        },
        {
          featureType: 'transit',
          elementType: 'geometry',
          stylers: [{ color: '#2f3948' }]
        },
        {
          featureType: 'transit.station',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#d59563' }]
        },
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers: [{ color: '#17263c' }]
        },
        {
          featureType: 'water',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#515c6d' }]
        },
        {
          featureType: 'water',
          elementType: 'labels.text.stroke',
          stylers: [{ color: '#17263c' }]
        }
      ];

      // Crea la mappa
      const mapOptions = {
        center: { lat: currentLat, lng: currentLng },
        zoom: 18, // Zoom maggiore
        styles: darkMapStyle,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      };

      mapRef.current = new window.google.maps.Map(mapContainerRef.current, mapOptions);

      // Funzione per creare/aggiornare il marker al centro
      const updateMarkerAtCenter = () => {
        const center = mapRef.current.getCenter();
        const lat = center.lat();
        const lng = center.lng();
        const markerDiv = document.createElement('div');
        markerDiv.innerHTML = `
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="28" fill="#EF4444" stroke="#fff" stroke-width="4"/>
            <circle cx="32" cy="32" r="12" fill="#fff" stroke="#EF4444" stroke-width="4"/>
          </svg>
        `;
        // Fallback: AdvancedMarkerElement o Marker classico
        if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
          if (markerRef.current) {
            markerRef.current.position = { lat, lng };
            markerRef.current.content = markerDiv;
            markerRef.current.map = mapRef.current;
          } else {
            markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
              map: mapRef.current,
              position: { lat, lng },
              content: markerDiv
            });
          }
        } else {
          // Marker classico
          if (markerRef.current) {
            markerRef.current.setPosition({ lat, lng });
            markerRef.current.setMap(mapRef.current);
          } else {
            markerRef.current = new window.google.maps.Marker({
              map: mapRef.current,
              position: { lat, lng },
              icon: {
                url: 'data:image/svg+xml;utf8,' + encodeURIComponent(`
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="28" fill="#EF4444" stroke="#fff" stroke-width="4"/>
                    <circle cx="32" cy="32" r="12" fill="#fff" stroke="#EF4444" stroke-width="4"/>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(64, 64),
                anchor: new window.google.maps.Point(32, 32)
              }
            });
          }
        }
      };

      // Inizializza marker al centro
      updateMarkerAtCenter();

      // Listener per mantenere il marker sempre al centro
      mapRef.current.addListener('center_changed', () => {
        updateMarkerAtCenter();
      });

    } catch (error) {
      console.error('❌ [InteractiveMap] Errore nell\'inizializzazione della mappa:', error);
      setHasError(true);
    }

    return () => {
      if (mapRef.current) {
        // Cleanup della mappa
        mapRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, [isGoogleMapsLoaded, currentLat, currentLng, GOOGLE_MAPS_CONFIG.API_KEY]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-zinc-400" />
        <label className="text-sm font-medium text-zinc-300">
          Mappa {required && <span className="text-red-400">*</span>}
        </label>
      </div>
      
      <div className="relative">
        {GOOGLE_MAPS_CONFIG.API_KEY && isGoogleMapsLoaded ? (
          <div 
            ref={mapContainerRef}
            className="w-full h-64 rounded-lg border border-zinc-600"
            style={{ minHeight: '256px' }}
          />
        ) : (
          <div className="w-full h-64 bg-zinc-800 border border-zinc-700 rounded-lg flex items-center justify-center">
            <div className="text-center text-zinc-500">
              <MapPin className="w-12 h-12 mx-auto mb-2 text-zinc-600" />
              <p className="text-sm">
                {!GOOGLE_MAPS_CONFIG.API_KEY 
                  ? 'Configura la chiave API per visualizzare la mappa'
                  : isLoading
                  ? 'Caricamento mappa Google Maps...'
                  : 'Errore nel caricamento della mappa'
                }
              </p>
            </div>
          </div>
        )}
        
        {disabled && (
          <div className="absolute inset-0 bg-zinc-900/50 rounded-lg flex items-center justify-center">
          </div>
        )}
      </div>
      
      {hasError && (
        <p className="text-xs text-red-400">
          Errore nel caricamento della mappa. Verifica la configurazione Google Maps.
        </p>
      )}
      
      {currentLat && currentLng && (
        <p className="text-xs text-zinc-500">
          Posizione: {currentLat.toFixed(6)}, {currentLng.toFixed(6)}
        </p>
      )}
    </div>
  );
}; 